import { useState, useEffect } from 'react';

// WebSocket client for streaming AgentCore responses
class TrendsWebSocketClient {
  constructor(websocketUrl, accessToken) {
    this.websocketUrl = websocketUrl;
    this.accessToken = accessToken;
    this.socket = null;
    this.onStatusUpdate = null;
    this.onResult = null;
    this.onError = null;
  }

  connect() {
    return new Promise((resolve, reject) => {
      try {
        console.log('Attempting to connect to WebSocket:', this.websocketUrl);
        console.log('Access token available:', !!this.accessToken);
        
        this.socket = new WebSocket(this.websocketUrl);
        
        this.socket.onopen = () => {
          console.log('WebSocket connected successfully');
          resolve();
        };
        
        this.socket.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            this.handleMessage(message);
          } catch (parseError) {
            console.error('Error parsing WebSocket message:', parseError, event.data);
          }
        };
        
        this.socket.onerror = (error) => {
          console.error('WebSocket error event:', error);
          console.error('WebSocket readyState:', this.socket?.readyState);
          console.error('WebSocket URL that failed:', this.websocketUrl);
          
          // Try to get more specific error information
          let errorMessage = 'WebSocket connection failed';
          if (error.target && error.target.readyState === WebSocket.CLOSED) {
            errorMessage = 'WebSocket connection was closed before it could be established';
          }
          
          if (this.onError) {
            this.onError(errorMessage);
          }
          reject(new Error(errorMessage));
        };
        
        this.socket.onclose = (event) => {
          console.log('WebSocket disconnected:', {
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean
          });
          
          // Common WebSocket close codes
          if (event.code === 1006) {
            console.error('WebSocket closed abnormally - possible network or server issue');
          } else if (event.code === 1011) {
            console.error('WebSocket closed due to server error');
          } else if (event.code === 1012) {
            console.error('WebSocket closed due to server restart');
          }
        };
        
      } catch (error) {
        console.error('Error creating WebSocket:', error);
        reject(error);
      }
    });
  }

  handleMessage(message) {
    console.log('Received message:', message);
    
    // Handle both Lambda messages and direct AgentCore messages
    switch (message.type) {
      case 'status':
        if (this.onStatusUpdate) {
          this.onStatusUpdate(message.message, message.status);
        }
        break;
        
      case 'keepalive':
        if (this.onStatusUpdate) {
          this.onStatusUpdate(message.message, message.status);
        }
        break;
        
      case 'partial_result':
        if (this.onPartialResult) {
          this.onPartialResult(message.data, message.chunk_count);
        }
        break;
        
      case 'result':
        if (this.onResult) {
          this.onResult(message.data);
        }
        break;
        
      case 'error':
        if (this.onError) {
          this.onError(message.message);
        }
        break;
        
      // Handle direct AgentCore streaming messages
      case 'agentcore_stream':
        if (this.onAgentCoreStream) {
          this.onAgentCoreStream(message.data, message.stream_type);
        }
        break;
        
      case 'agentcore_progress':
        if (this.onStatusUpdate) {
          this.onStatusUpdate(message.message || 'AgentCore processing...', 'streaming');
        }
        break;
        
      case 'agentcore_result':
        if (this.onResult) {
          this.onResult(message.data);
        }
        break;
        
      case 'agentcore_error':
        if (this.onError) {
          this.onError(message.message || 'AgentCore error occurred');
        }
        break;
        
      case 'agentcore_complete':
        if (this.onStatusUpdate) {
          this.onStatusUpdate('AgentCore processing completed', 'completed');
        }
        if (this.onResult && message.data) {
          this.onResult(message.data);
        }
        break;
        
      default:
        console.log('Unknown message type:', message.type, message);
        
        // Handle OpenTelemetry log format from AgentCore
        if (message.body && message.body.message && message.body.message.content) {
          console.log('Detected OpenTelemetry format message');
          try {
            const content = message.body.message.content[0];
            if (content && content.type === 'text' && content.text) {
              // Extract JSON from markdown code blocks
              const jsonMatch = content.text.match(/```json\n([\s\S]*?)\n```/);
              if (jsonMatch) {
                const trendsData = JSON.parse(jsonMatch[1]);
                console.log('Parsed trends from OpenTelemetry format:', trendsData);
                if (this.onResult) {
                  this.onResult(trendsData);
                }
                return;
              }
            }
          } catch (parseError) {
            console.error('Error parsing OpenTelemetry message:', parseError);
          }
        }
        
        // Try to handle as generic AgentCore message
        if (message.data && this.onResult) {
          this.onResult(message.data);
        }
    }
  }

  processTrends(inputText = '') {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket not connected');
    }
    
    const message = {
      action: 'process-trends',
      authorization: this.accessToken,
      inputText: inputText,
      sessionId: `session-${Date.now()}`
    };
    
    this.socket.send(JSON.stringify(message));
  }

  validateTrends(trendsToValidate) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket not connected');
    }
    
    const message = {
      action: 'validate-trends',
      authorization: this.accessToken,
      trends: trendsToValidate,
      sessionId: `validation-session-${Date.now()}`
    };
    
    this.socket.send(JSON.stringify(message));
  }

  disconnect() {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
  }
}

// Custom hook for WebSocket trends
export const useWebSocketTrends = (websocketUrl, accessToken) => {
  const [client, setClient] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [status, setStatus] = useState('');
  const [result, setResult] = useState(null);
  const [partialResults, setPartialResults] = useState([]);
  const [agentCoreStreams, setAgentCoreStreams] = useState([]);
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (websocketUrl && accessToken) {
      console.log('Initializing WebSocket client with:', { websocketUrl, hasToken: !!accessToken });
      const wsClient = new TrendsWebSocketClient(websocketUrl, accessToken);
      
      wsClient.onStatusUpdate = (message, statusType) => {
        setStatus(message);
        setError('');
        setIsProcessing(
          statusType === 'processing' || 
          statusType === 'calling_agent' || 
          statusType === 'streaming' ||
          statusType === 'invoking_agent' ||
          statusType === 'agent_invoked'
        );
      };
      
      wsClient.onPartialResult = (data, chunkCount) => {
        console.log('Received partial result:', data, 'Chunk:', chunkCount);
        setPartialResults(prev => [...prev, { data, chunkCount, timestamp: Date.now() }]);
        setStatus(`Streaming data... (chunk ${chunkCount})`);
      };
      
      wsClient.onAgentCoreStream = (data, streamType) => {
        console.log('Received AgentCore stream:', data, 'Type:', streamType);
        setAgentCoreStreams(prev => [...prev, { data, streamType, timestamp: Date.now() }]);
        setStatus(`AgentCore streaming... (${streamType})`);
      };
      
      wsClient.onResult = (data) => {
        setResult(data);
        setStatus('Completed successfully');
        setIsProcessing(false);
      };
      
      wsClient.onError = (errorMessage) => {
        setError(errorMessage);
        setStatus('Error occurred');
        setIsProcessing(false);
      };
      
      wsClient.connect()
        .then(() => {
          setIsConnected(true);
          setClient(wsClient);
        })
        .catch((err) => {
          const errorMessage = err?.message || err?.toString() || 'Unknown connection error';
          console.error('WebSocket connection failed:', err);
          setError(`Connection failed: ${errorMessage}`);
          setIsConnected(false);
        });
      
      return () => {
        wsClient.disconnect();
        setIsConnected(false);
        setClient(null);
      };
    }
  }, [websocketUrl, accessToken]);

  const processTrends = (inputText) => {
    if (client && isConnected) {
      setResult(null);
      setPartialResults([]);
      setAgentCoreStreams([]);
      setError('');
      setStatus('Starting trends processing...');
      setIsProcessing(true);
      client.processTrends(inputText);
    } else {
      setError('WebSocket not connected');
    }
  };

  const validateTrends = (trendsToValidate) => {
    if (client && isConnected) {
      setError('');
      setStatus('Validating trends...');
      setIsProcessing(true);
      client.validateTrends(trendsToValidate);
    } else {
      setError('WebSocket not connected');
    }
  };

  return {
    isConnected,
    status,
    result,
    partialResults,
    agentCoreStreams,
    error,
    isProcessing,
    processTrends,
    validateTrends
  };
};